/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AddGitToSoftwareModules.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions, settingsApis, labCar } from "../../redux/actions";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import ProjectVariantSearchbar from "../../components/customComponentsV2/common/projectSearchbar/ProjectvariantSearchbar";
import { toast } from "react-toastify";
import VlcCardContent from "../../components/customComponentsV2/vlc/cards/VlcCardContent";
import { vlcCardStyles } from "../../components/customComponentsV2/vlc/config/vlcConfig";
import { componentStyle, fontSizeDetails } from "../../config/appTheme";
import ComponentCard from "../regulatory-compliance/Link/ComponentCard";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";

/* ---------------- HELPERS ---------------- */
const isValidGitUrl = (url) => url?.toLowerCase().includes("git");

const resolveMetaStatus = (swm) => {
  if (!swm?.meta_details) return "NEW";
  return swm.meta_details.meta_status || "NEW";
};

function AddGitIntegrations({ open, onClose }) {
  const dispatch = useDispatch();

  /* ---------------- REDUX ---------------- */
  const selectedProject = useSelector(
    (state) => state.projectConfigReducer.selectedProjectDetails
  );
  const selectedVariant = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails
  );

  /* ---------------- EE STATE ---------------- */
  const [eeList, setEeList] = useState([]);
  const [selectedEE, setSelectedEE] = useState(null);
  const [eePage, setEePage] = useState(1);
  const [eeRowsPerPage] = useState(25);
  const [eeCount, setEeCount] = useState(0);

  /* ---------------- SWM STATE ---------------- */
  const [swmList, setSwmList] = useState([]);
  const [selectedSWM, setSelectedSWM] = useState(null);

  /* ---------------- GIT STATE ---------------- */
  const [gitUrl, setGitUrl] = useState("");
  const [gitUrlError, setGitUrlError] = useState("");
  const [gitToken, setGitToken] = useState("");
  const [gitTokenError, setGitTokenError] = useState("");
  const [metaStatus, setMetaStatus] = useState("NEW");

  const isEditBlocked = metaStatus === "ACTIVE";
  const hasGitFromDB = Boolean(selectedSWM?.meta_details?.linked_meta_link);
  const isEditBlockedGit = hasGitFromDB || metaStatus === "ACTIVE";

  /* ---------------- PROJECT CHANGE RESET ---------------- */
  useEffect(() => {
    setEeList([]);
    setEeCount(0);
    setSelectedEE(null);
    setSwmList([]);
    setSelectedSWM(null);
  }, [selectedProject?.project_id, selectedVariant?.variant_id]);

  /* ---------------- EE SEARCH PARAMS ---------------- */
  const eeSearchParams = useMemo(
    () => ({
      project_id: Number(selectedProject?.project_id),
      variant_id: Number(selectedVariant?.variant_id),
      page_number: eePage,
      page_size: eeRowsPerPage,
      status: "true",
    }),
    [selectedProject, selectedVariant, eePage, eeRowsPerPage]
  );

  /* ---------------- EE CARD DATA ---------------- */
  const eeCardData = useMemo(
    () =>
      eeList.map((item) => ({
        id: item.ee_id,
        data: item,
        cardDataArray: [
          {
            key: "Number",
            value: item.assembly_number,
            keyStyle: { fontWeight: 600 },
            valueStyle: { fontWeight: 600 },
          },
          {
            key: "Description",
            value: item.description,
          },
          {
            key: "Version",
            value: item.version,
          },
        ],
      })),
    [eeList]
  );

  /* ---------------- LOAD SWM ---------------- */
  const loadSoftwareModules = async (eeId) => {
    const resp = await dispatch(
      HandleApiActions({
        ...labCar.get_sw_modules,
        params: {
          project_id: selectedProject.project_id,
          variant_id: selectedVariant.variant_id,
          ee_id: eeId,
          status: true,
        },
        show_toast: false,
      })
    );

    if (!resp?.error && resp?.data?.length) {
      setSwmList(resp.data);
      setSelectedSWM(resp?.data[0]);
    } else {
      setSwmList([]);
      setSelectedSWM(null);
    }
  };

  /* ---------------- AUTO SELECT FIRST EE ---------------- */
  useEffect(() => {
    if (!eeList.length) return;

    const exists = eeList.some((ee) => ee.ee_id === selectedEE?.ee_id);

    if (!selectedEE || !exists) {
      const firstEE = eeList[0];
      setSelectedEE(firstEE);
      loadSoftwareModules(firstEE.ee_id);
    }
  }, [eeList]);

  /* ---------------- AUTO SELECT FIRST SWM ---------------- */
  useEffect(() => {
    if (!swmList.length) return;

    if (!selectedSWM) {
      setSelectedSWM(swmList[0]);
    }
  }, [swmList]);

  /* ---------------- SWM CHANGE ---------------- */
  useEffect(() => {
    if (!selectedSWM) return;

    const meta = selectedSWM.meta_details;
    setGitUrl(meta?.linked_meta_link || "");
    setGitToken(meta?.meta_token || "");
    setGitUrlError("");
    setGitTokenError("");
    setMetaStatus(resolveMetaStatus(selectedSWM));
  }, [selectedSWM]);

  /* ---------------- SAVE ---------------- */
  const handleSaveGit = async () => {
    let hasError = false;

    if (!isValidGitUrl(gitUrl)) {
      setGitUrlError("Please enter a valid Git URL");
      hasError = true;
    }

    if (!gitToken && metaStatus !== "ACTIVE") {
      setGitTokenError("Git access token is required");
      hasError = true;
    }

    if (hasError) return;

    const resp = await dispatch(
      HandleApiActions({
        ...settingsApis.add_git_webhook,
        params: {
          swm_id: selectedSWM.swm_id,
          swm_version_id: selectedSWM?.sw_version?.swm_version_id,
          meta_url: gitUrl,
          meta_token: gitToken,
        },
        show_toast: false,
      })
    );

    if (resp?.error) {
      toast.error("Git saved, but webhook failed.");
      setMetaStatus("FAILED");
      return;
    }

    const newStatus = resp?.data?.meta_status || "PENDING";
    setMetaStatus(newStatus);

    toast.success(
      newStatus === "ACTIVE"
        ? "Git integration activated successfully"
        : "Git saved. Webhook pending."
    );
  };

  const isSaveDisabled = () => {
    if (!selectedEE || !selectedSWM) return true;
    if (isEditBlocked) return true;
    if (!gitUrl || !gitToken) return true;
    if (gitUrlError || gitTokenError) return true;
    return false;
  };

  /* ---------------- UI ---------------- */
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ fontSize: "1.1rem" }}>Add Git Integration</DialogTitle>

      <DialogContent dividers>
        <Box mb={2}>
          <ProjectVariantSearchbar />
        </Box>

        <Grid container spacing={1}>
          {/* EE COMPONENTS */}
          <Grid item xs={12} md={3}>
            <ComponentCard
              width="100%"
              headerTitle={
                <CustomSearchbarWithApiV2
                  apiAction={labCar.get_ee_components_new}
                  searchKey="assembly_number"
                  placeholder="Search EE Component"
                  params={eeSearchParams}
                  txtFldStyle={componentStyle.searchField.textField}
                  getDataOnSearch={(data, resp) => {
                    if (resp?.error) {
                      setEeList([]);
                      setEeCount(0);
                      setSelectedEE(null);
                      setSwmList([]);
                      setSelectedSWM(null);
                    } else {
                      setEeList(resp.data);
                      setEeCount(resp.total_count);
                    }
                  }}
                />
              }
              dataList={eeCardData}
              selectedComponentDetails={selectedEE}
              handleSelectedComponent={(obj) => {
                setSelectedEE(obj.data);
                loadSoftwareModules(obj.data.ee_id);
              }}
              page={eePage}
              rowsPerPage={eeRowsPerPage}
              totalCount={eeCount}
              handlePageChange={(p) => setEePage(p)}
              renderCardActionIcons={() => null}
              idKey="ee_id"
            />
          </Grid>

          {/* SOFTWARE MODULES */}
          <Grid item xs={12} md={3}>
            <Typography
              fontWeight={600}
              mb={1}
              sx={{ ...fontSizeDetails.fontSizeMedium }}
            >
              Software Modules
            </Typography>
            {swmList?.length === 0 && (
              <Typography
                align="center"
                color="text.secondary"
                sx={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                No Software Modules found for the selected EE Component
              </Typography>
            )}
            <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
              {swmList.map((swm) => (
                <VlcCardContent
                  key={swm.swm_id}
                  isSelected={selectedSWM?.swm_id === swm.swm_id}
                  onClick={() => setSelectedSWM(swm)}
                >
                  <Box p={1.5}>
                    <Typography
                      sx={{
                        fontSize: "14px",

                        fontWeight: 500,
                        color: vlcCardStyles.vlcCardListColor,
                      }}
                    >
                      {swm.name}
                    </Typography>
                    <Typography variant="caption">
                      Version: {swm.version}
                    </Typography>
                  </Box>
                </VlcCardContent>
              ))}
            </Box>
          </Grid>

          {/* GIT CONFIG */}
          <Grid item xs={12} md={6}>
            <Typography
              fontWeight={600}
              mb={1}
              sx={{ ...fontSizeDetails.fontSizeMedium }}
            >
              Git Details
            </Typography>
            {selectedSWM ? (
              <Box
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  p: 1,
                  backgroundColor: "#fafafa",
                }}
              >
                <CustomFormTextFieldV2
                  label="Repository URL"
                  value={gitUrl}
                  setValue={(val) => {
                    setGitUrl(val);
                    setGitUrlError(
                      val && !isValidGitUrl(val)
                        ? "Please enter a valid Git URL"
                        : ""
                    );
                  }}
                  valueError={gitUrlError}
                  disabled={isEditBlockedGit}
                />

                <Box mt={2} />

                <CustomFormTextFieldV2
                  label="Access Token"
                  type="password"
                  value={gitToken}
                  setValue={(val) => {
                    setGitToken(val);
                    setGitTokenError(val ? "" : "Git access token is required");
                  }}
                  valueError={gitTokenError}
                  disabled={isEditBlocked}
                />
              </Box>
            ) : (
              <Typography
                align="center"
                color="text.secondary"
                sx={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Select a Software Module to configure Git
              </Typography>
            )}
            {/* STATUS MESSAGES */}{" "}
            <Box mt={1}>
              {" "}
              {metaStatus === "ACTIVE" && (
                <Typography
                  variant="caption"
                  color="success.main"
                  sx={{ fontSize: fontSizeDetails.body1.fontSize }}
                >
                  {" "}
                  Git webhook integration is active.{" "}
                </Typography>
              )}{" "}
              {metaStatus === "PENDING" && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ fontSize: fontSizeDetails.body1.fontSize }}
                >
                  {" "}
                  The webhook setup is pending. Please check the Git URL or
                  authentication token and retry.{" "}
                </Typography>
              )}{" "}
              {metaStatus === "FAILED" && (
                <Typography
                  variant="caption"
                  color="error.main"
                  sx={{ fontSize: fontSizeDetails.body1.fontSize }}
                >
                  {" "}
                  Webhook failed. Please check the Git URL or authentication
                  token and retry.{" "}
                </Typography>
              )}{" "}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <CustomButtonV2
          type="cancel"
          btnName="Cancel"
          handleOnClick={onClose}
        />
        <CustomButtonV2
          btnName={metaStatus === "FAILED" ? "Retry" : "Save"}
          disabled={isSaveDisabled()}
          handleOnClick={handleSaveGit}
        />
      </DialogActions>
    </Dialog>
  );
}

export default AddGitIntegrations;
